import { ApiFetchError } from "@/lib/apiFetch";

/**
 * Returns true when an error value indicates the server rejected the request
 * with HTTP 401 — meaning the staff member's portal token has expired
 * mid-session and they need a fresh link.
 *
 * Handles two error shapes:
 *  • ApiFetchError (thrown by apiFetch) — check .status === 401
 *  • Generic Error with message "Session expired" — thrown by raw-fetch
 *    query functions that explicitly detect a 401 response
 *
 * Intentionally excludes 403 / 5xx so the intercept never fires for
 * routes that deliberately handle 401 or for transient server errors.
 */
export function isSessionExpiredError(error: unknown): boolean {
  if (error instanceof ApiFetchError && error.status === 401) return true;
  if (error instanceof Error && error.message === "Session expired") return true;
  return false;
}

/**
 * Returns true if any of the supplied query errors represents a mid-session
 * 401 expiry. Pass the `error` fields from every useQuery call on the page.
 *
 * Usage:
 *   const sessionExpired = useSessionExpired([infoQuery.error, bookingsQuery.error]);
 */
export function useSessionExpired(errors: unknown[]): boolean {
  return errors.some(isSessionExpiredError);
}
